import React, { useMemo, useState, useCallback } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ScrollView } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { RESIDENTS_DESKTOP_COLORS } from '@/constants/residents/desktop/color';
import { usePaymentData } from '@/features/payments/hooks/usePaymentData';
import { hasPaymentMethodCardExpired } from '@/utils/residents/paymentUtils';
import PaymentMethodCard from '@/components/residents/payments/PaymentMethodCard';
import { WebConfirmModal } from '@/components/common/modals/desktop/DesktopConfirmModal';
import { DesktopSectionHeader } from '@/components/residents/desktop/myAccount/DesktopSectionHeader';
import { useConfirmModal } from '@/hooks/common/useConfirmModal';
import {
  usePutBankAccountMutation,
  usePutCreditCardMutation,
  useDeleteBankAccountMutation,
  useDeleteCreditCardMutation,
} from '@/store/api';
import { useToast } from '@/contexts/admin/ToastContext';
import PaymentMethodsGrid from '@/components/residents/payments/PaymentMethodsGrid';
import { strings, formatString } from '@/constants/residents/strings';

export const PaymentMethodsView: React.FC = () => {
  const navigation = useNavigation<any>();
  const { paymentMethods, isLoading, refreshPaymentMethods } = usePaymentData();
  const { showSuccess, showError } = useToast();

  // Mutations for manage actions
  const [putBankAccount, { isLoading: isUpdatingBank }] = usePutBankAccountMutation();
  const [putCreditCard, { isLoading: isUpdatingCard }] = usePutCreditCardMutation();
  const [deleteBankAccount, { isLoading: isDeletingBank }] = useDeleteBankAccountMutation();
  const [deleteCreditCard, { isLoading: isDeletingCard }] = useDeleteCreditCardMutation();

  const isUpdating = isUpdatingBank || isUpdatingCard;
  const isDeleting = isDeletingBank || isDeletingCard;

  // Delete confirmation modal
  const { visible: confirmVisible, open: openConfirm, close: closeConfirm } = useConfirmModal(false);
  const [methodToDelete, setMethodToDelete] = useState<any>(null);

  const sortedMethods = useMemo(() => {
    return [...paymentMethods].sort(({ isDefault: x = false }, { isDefault: y = false }) => Number(y) - Number(x));
  }, [paymentMethods]);

  const onSetAsDefault = useCallback(async (method: any) => {
    try {
      if (method.type === 'bankAccount' || method.accttype === 'ECHK' || method.accttype === 'ESAV' || method.accttype === 'bankAccount') {
        await putBankAccount({
          sourceId: String(method.sourceId || method.id),
          bankAccount: { isDefault: true },
        }).unwrap();
      } else {
        await putCreditCard({
          sourceId: String(method.sourceId || method.id),
          creditCard: { isDefault: true },
        }).unwrap();
      }
      refreshPaymentMethods();
      showSuccess('Default payment method updated');
    } catch (_e) {
      showError('Failed to set default payment method');
    }
  }, [putBankAccount, putCreditCard, refreshPaymentMethods]);

  const onRequestDelete = useCallback((method: any) => {
    setMethodToDelete(method);
    openConfirm();
  }, [openConfirm]);

  const onConfirmDelete = useCallback(async () => {
    if (!methodToDelete) return;
    try {
      if (methodToDelete.type === 'bankAccount' || methodToDelete.accttype === 'ECHK' || methodToDelete.accttype === 'ESAV' || methodToDelete.accttype === 'bankAccount') {
        await deleteBankAccount({ sourceId: String(methodToDelete.sourceId || methodToDelete.id) }).unwrap();
      } else {
        await deleteCreditCard({ sourceId: String(methodToDelete.sourceId || methodToDelete.id) }).unwrap();
      }
      closeConfirm();
      setMethodToDelete(null);
      refreshPaymentMethods();
      showSuccess('Payment method removed');
    } catch (_e) {
      closeConfirm();
      setMethodToDelete(null);
      showError('Failed to remove payment method');
    }
  }, [methodToDelete, deleteBankAccount, deleteCreditCard, refreshPaymentMethods, closeConfirm]);

  const handleAddCard = () => {
    navigation.navigate('AddOrChangePaymentMethod', { paymentType: 'card', managePaymentPage: 'ManageMyWallet' });
  };

  const handleAddACH = () => {
    navigation.navigate('AddOrChangePaymentMethod', { paymentType: 'ach', managePaymentPage: 'ManageMyWallet' });
  };

  const hasNoMethods = sortedMethods.length === 0 && !isLoading;

  return (
    <View style={styles.root}>
      <DesktopSectionHeader
        title={strings.account.paymentMethodsTitle}
        containerStyle={styles.header}
        titleStyle={styles.headerTitle}
      />

      <View style={styles.content}>
        {hasNoMethods ? (
          <View style={styles.emptyState}>
            <Text style={styles.emptyTitle}>{strings.account.paymentMethodsEmptyTitle}</Text>
            <Text style={styles.emptyMessage}>{strings.account.paymentMethodsEmptyMessage}</Text>
          </View>
        ) : (
          <PaymentMethodsGrid
            methods={sortedMethods}
            isUpdating={isUpdating}
            onSetAsDefault={onSetAsDefault}
            onDelete={onRequestDelete}
          />
        )}
      </View>

      <View style={styles.footer}>
        <View style={styles.addRow}>
          <Text style={styles.addTitle}>{strings.account.paymentMethodsAddTitle}</Text>
          <View style={styles.addButtons}>
            <TouchableOpacity style={styles.addBtn} onPress={handleAddCard}><Text style={styles.addBtnText}>{strings.account.paymentMethodsAddCard}</Text></TouchableOpacity>
            <TouchableOpacity style={styles.addBtn} onPress={handleAddACH}><Text style={styles.addBtnText}>{strings.account.paymentMethodsAddACH}</Text></TouchableOpacity>
          </View>
        </View>
      </View>

      <WebConfirmModal
        visible={confirmVisible}
        title={strings.account.paymentMethodsConfirmDeleteTitle}
        message={formatString(strings.account.paymentMethodsConfirmDeleteMessage, (methodToDelete?.name || (methodToDelete?.last4 ? `•••• ${methodToDelete?.last4}` : 'this payment method')) || '')}
        confirmText={strings.alert.delete}
        cancelText={strings.alert.cancel}
        onConfirm={onConfirmDelete}
        onCancel={() => { closeConfirm(); setMethodToDelete(null); }}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  root: { flex: 1, backgroundColor: RESIDENTS_DESKTOP_COLORS.PRIMARY_BACKGROUND },
  header: { borderBottomWidth: 1, borderBottomColor: RESIDENTS_DESKTOP_COLORS.TEXT_TERTIARY, paddingHorizontal: 20, paddingVertical: 14 },
  headerTitle: { color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY, fontWeight: '700', fontSize: 18 },
  content: { flex: 1, padding: 24 },
  cardsScroll: { flex: 1 },
  methodsGrid: { flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'space-between' },
  methodItem: { width: '48%', marginBottom: 16 },
  emptyState: { paddingVertical: 48, alignItems: 'center', gap: 8 },
  emptyTitle: { color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY, fontSize: 14, fontWeight: '600' },
  emptyMessage: { color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY, opacity: 0.7, fontSize: 12, textAlign: 'center', maxWidth: 520 },
  footer: { borderTopWidth: 1, borderTopColor: RESIDENTS_DESKTOP_COLORS.TEXT_TERTIARY, paddingHorizontal: 24, paddingVertical: 16 },
  addRow: { gap: 12 },
  addTitle: { color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY, fontSize: 16, fontWeight: '600' },
  addButtons: { flexDirection: 'column', gap: 12 },
  addBtn: { borderWidth: 1, borderColor: RESIDENTS_DESKTOP_COLORS.TEXT_TERTIARY, paddingHorizontal: 12, paddingVertical: 8, borderRadius: 10 },
  addBtnText: { color: RESIDENTS_DESKTOP_COLORS.TEXT_PRIMARY, fontWeight: '600' },
});

export default PaymentMethodsView;


